import { Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { ReactNode, useEffect, useRef, useState } from 'react'

import cn from '@/utilities/cn'

import { CheckFilled } from './Icons'

// @ts-expect-error
function useComponentVisible(initialIsVisible) {
  const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible)
  const ref = useRef(null)

  function handleClickOutside(event: { target: any }) {
    // @ts-expect-error
    if (ref.current && !ref.current.contains(event.target)) {
      setIsComponentVisible(false)
    }
  }
  useEffect(() => {
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])
  return { ref, isComponentVisible, setIsComponentVisible }
}

type GuideModalProps = {
  isShowingGuide?: boolean
  header: string
  message: ReactNode
  subMessage?: string
  translate?: string
}

const GuideModal = ({ isShowingGuide, header, message, subMessage, translate }: GuideModalProps) => {
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(true)

  useEffect(() => {
    isShowingGuide ? setIsComponentVisible(true) : setIsComponentVisible(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowingGuide])

  return (
    <div className="z-[5] h-0 w-full">
      <div
        aria-live="assertive"
        className={cn('pointer-events-none relative inset-0 z-[5] flex items-end sm:items-start', translate)}
      >
        <div ref={ref} className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={isComponentVisible}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="pointer-events-auto w-full max-w-xs">
              <div className="flex justify-between">
                <div className="-m-px w-3 py-2 lg:block">
                  <svg
                    className="h-full w-full -scale-x-100 bg-transparent text-[#364253]"
                    viewBox="0 0 12 82"
                    fill="#364253"
                    preserveAspectRatio="none"
                  >
                    <path d="M0.5 0V31L10.5 41L0.5 51V82" stroke="currentcolor" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
                <div className="flex w-full items-start rounded-lg bg-[#364253] p-3">
                  <div className="flex-shrink-0">
                    <CheckFilled className="h-6 w-6 text-gray-400" aria-hidden="true" style={{ color: '#00dc81' }} />
                  </div>
                  <div className="ml-3 w-0 flex-1">
                    <p className="text-lg font-bold text-[#EAECEF]">{header}</p>
                    <div className="block text-sm text-[#EAECEF]">{message}</div>
                    {subMessage && <div className="block pt-1 text-sm text-[#EAECEF]">{subMessage}</div>}
                  </div>
                  <div className="ml-4 flex flex-shrink-0">
                    <button
                      type="button"
                      className="inline-flex rounded-md bg-[#364253] text-[#EAECEF] hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => {
                        setIsComponentVisible(false)
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  )
}

export default GuideModal
